/**
 * Education & Nurture Sequence Templates
 * Based on best practices from HubSpot, Mailchimp, Canva, Notion
 *
 * Key insights from billion-dollar companies:
 * - Education-first approach increases conversion 3x
 * - Weekly tips emails have 40%+ open rates
 * - Use case content drives feature adoption
 * - Thought leadership builds brand loyalty
 */

import { SequenceTemplate } from "./types";

export const WEEKLY_TIPS_SERIES: SequenceTemplate = {
  id: "weekly-tips",
  name: "Weekly Tips & Best Practices",
  type: "education",
  description: "Regular tips to help users get more value from your product",
  strategy: "Consistent valuable content keeps you top-of-mind. HubSpot's weekly tips have 45% open rates. Focus on actionable advice that shows quick wins.",
  icon: "BulbOutlined",
  color: "#f59e0b",
  expectedOutcomes: [
    "40%+ email open rates",
    "Increased product usage",
    "Reduced support tickets",
    "Brand thought leadership"
  ],
  bestPractices: [
    "Send same day/time each week",
    "One actionable tip per email",
    "Include screenshot or video",
    "Link to full tutorial",
    "Track which tips drive engagement"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 7, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Week 1 - Quick Win",
      subject: "Tip #1: Double your AI copilot output in 5 minutes",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "First tip - immediate value",
      variables: [
        { key: "tip_title", defaultValue: "Quick Response Templates" },
        { key: "tip_benefit", defaultValue: "Save 2 hours/week" }
      ]
    },
    {
      name: "Week 2 - Power Feature",
      subject: "Tip #2: The hidden feature pros use",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Introduce power user feature"
    },
    {
      name: "Week 3 - Integration",
      subject: "Tip #3: Connect to your favorite tools",
      suggestedTemplateId: "product-announcement",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Drive integration adoption"
    },
    {
      name: "Week 4 - Analytics",
      subject: "Tip #4: Understand your AI copilot performance",
      suggestedTemplateId: "monthly-analytics",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Analytics feature education"
    },
    {
      name: "Week 5 - Advanced",
      subject: "Tip #5: Advanced tactics from power users",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Advanced strategies"
    },
    {
      name: "Week 6 - Optimization",
      subject: "Tip #6: Optimize for conversions",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Conversion optimization"
    }
  ],
  tags: ["education", "tips", "engagement", "weekly"],
  bestFor: ["All SaaS products", "Complex tools", "Products with learning curve"],
  difficulty: "beginner",
  setupTime: "25 minutes"
};

export const USE_CASE_INSPIRATION: SequenceTemplate = {
  id: "use-case-inspiration",
  name: "Use Case Inspiration Series",
  type: "education",
  description: "Show different ways to use your product",
  strategy: "Users often don't know what's possible. Canva grew by showing endless use cases. Inspire users with creative applications they hadn't considered.",
  icon: "AppstoreOutlined",
  color: "#3b82f6",
  expectedOutcomes: [
    "30% increase in feature usage",
    "New use case discovery",
    "Reduced churn (more value found)",
    "Cross-sell opportunities"
  ],
  bestPractices: [
    "Feature real customer examples",
    "Include templates/starting points",
    "Segment by industry/role",
    "Show before/after results",
    "Make it easy to replicate"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 14, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "medium"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Legal Copilot",
      subject: "Use Case: Draft legal documents in seconds",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Legal AI copilot use case",
      variables: [
        { key: "use_case", defaultValue: "Legal Documents" },
        { key: "result", defaultValue: "80% faster document drafting" }
      ]
    },
    {
      name: "Marketing Copilot",
      subject: "Use Case: Create marketing content in minutes",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Marketing AI copilot use case"
    },
    {
      name: "Email Copilot",
      subject: "Use Case: Write professional emails instantly",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Email writing AI copilot use case"
    },
    {
      name: "Code Copilot",
      subject: "Use Case: Generate code snippets quickly",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Coding AI copilot use case"
    },
    {
      name: "Research Copilot",
      subject: "Use Case: Summarize research in seconds",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Research AI copilot use case"
    }
  ],
  tags: ["use-cases", "inspiration", "education", "templates"],
  bestFor: ["Versatile products", "Platform tools", "Products with multiple use cases"],
  difficulty: "intermediate",
  setupTime: "20 minutes"
};

export const PRODUCT_MASTERY_COURSE: SequenceTemplate = {
  id: "product-mastery",
  name: "Product Mastery Course",
  type: "education",
  description: "Turn new users into power users with a structured course",
  strategy: "Structured learning increases activation 50%. Break product into modules. Certificate completion drives engagement. This is your product academy.",
  icon: "TrophyOutlined",
  color: "#8b5cf6",
  expectedOutcomes: [
    "50% higher activation",
    "Power user development",
    "Reduced support needs",
    "Community champions"
  ],
  bestPractices: [
    "Structure as progressive modules",
    "Include quizzes/checkpoints",
    "Offer completion certificate",
    "Mix video, text, and hands-on",
    "Celebrate progress milestones"
  ],
  trigger: {
    eventType: "signup",
    delay: { value: 3, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active", "trialing"]
    }
  },
  emails: [
    {
      name: "Course Introduction",
      subject: "{{first_name}}, become a {{product_name}} expert in 7 days",
      suggestedTemplateId: "onboarding-day1",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Introduce mastery course",
      variables: [
        { key: "course_name", defaultValue: "Copilotly Mastery" },
        { key: "modules", defaultValue: "7" }
      ]
    },
    {
      name: "Module 1 - Basics",
      subject: "Module 1: Your first AI generation",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 1, unit: "days" },
      purpose: "First module - fundamentals"
    },
    {
      name: "Module 2 - AI Models",
      subject: "Module 2: Choosing the right AI model",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 1, unit: "days" },
      purpose: "AI models module"
    },
    {
      name: "Module 3 - Integrations",
      subject: "Module 3: Connecting your tools",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 1, unit: "days" },
      purpose: "Integrations module"
    },
    {
      name: "Module 4 - Analytics",
      subject: "Module 4: Measuring success",
      suggestedTemplateId: "monthly-analytics",
      delayAfterPrevious: { value: 1, unit: "days" },
      purpose: "Analytics module"
    },
    {
      name: "Module 5 - Advanced",
      subject: "Module 5: Advanced automation",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 1, unit: "days" },
      purpose: "Advanced features module"
    },
    {
      name: "Course Completion",
      subject: "Congratulations! You're now a {{product_name}} expert",
      suggestedTemplateId: "product-launch",
      delayAfterPrevious: { value: 1, unit: "days" },
      purpose: "Celebrate completion + certificate"
    }
  ],
  tags: ["education", "course", "mastery", "onboarding"],
  bestFor: ["Complex products", "Products with learning curve", "B2B SaaS"],
  difficulty: "advanced",
  setupTime: "30 minutes"
};

export const WEBINAR_NURTURE: SequenceTemplate = {
  id: "webinar-nurture",
  name: "Webinar Registration & Follow-up",
  type: "education",
  description: "Drive webinar registrations and follow up with attendees",
  strategy: "Webinars convert at 20-40% to paid. Use email to drive registration, send reminders, and follow up with recording. This is top-of-funnel gold.",
  icon: "VideoCameraOutlined",
  color: "#ef4444",
  expectedOutcomes: [
    "30%+ registration rate",
    "50%+ attendance rate",
    "20-40% conversion to action",
    "Lead nurturing at scale"
  ],
  bestPractices: [
    "Send 3 reminders before webinar",
    "Include add-to-calendar links",
    "Send recording within 1 hour",
    "Follow up with related offer",
    "Segment by attendance"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 0, unit: "minutes" }
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active", "trialing"]
    }
  },
  emails: [
    {
      name: "Webinar Invitation",
      subject: "You're invited: {{webinar_title}}",
      suggestedTemplateId: "invitation",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Initial webinar invitation",
      variables: [
        { key: "webinar_title", defaultValue: "Master AI Copilot Productivity" },
        { key: "webinar_date", defaultValue: "{{event_date}}" },
        { key: "webinar_time", defaultValue: "{{event_time}}" }
      ]
    },
    {
      name: "Registration Confirmation",
      subject: "You're registered! See you at {{webinar_title}}",
      suggestedTemplateId: "confirmation",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Confirm registration + calendar link"
    },
    {
      name: "1 Day Reminder",
      subject: "Tomorrow: {{webinar_title}} - Don't miss it!",
      suggestedTemplateId: "product-reminder",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Day before reminder"
    },
    {
      name: "1 Hour Reminder",
      subject: "Starting in 1 hour: {{webinar_title}}",
      suggestedTemplateId: "product-reminder",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Hour before reminder with join link"
    },
    {
      name: "Recording + Follow-up",
      subject: "Recording: {{webinar_title}} + Special Offer",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Send recording + next step offer"
    }
  ],
  tags: ["webinar", "events", "education", "lead-gen"],
  bestFor: ["B2B SaaS", "Products requiring demos", "Complex sales cycles"],
  difficulty: "intermediate",
  setupTime: "20 minutes"
};

export const PRODUCT_UPDATES_DIGEST: SequenceTemplate = {
  id: "product-updates",
  name: "Product Updates & Changelog",
  type: "education",
  description: "Keep users informed about new features and improvements",
  strategy: "Regular updates show product momentum and give re-engagement reasons. Notion and Linear's changelogs are legendary. Make users excited about what's new.",
  icon: "RocketOutlined",
  color: "#10b981",
  expectedOutcomes: [
    "Increased feature adoption",
    "Re-engagement of dormant users",
    "Reduced churn (product improving)",
    "Positive brand perception"
  ],
  bestPractices: [
    "Send monthly or bi-weekly",
    "Lead with biggest feature",
    "Include visual demos",
    "Segment by relevance",
    "Link to detailed release notes"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 30, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Monthly Product Update",
      subject: "What's new in {{product_name}} - {{month}} Edition",
      suggestedTemplateId: "product-announcement",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Monthly feature roundup",
      variables: [
        { key: "month", defaultValue: "{{current_month}}" },
        { key: "top_feature", defaultValue: "{{new_feature_name}}" },
        { key: "feature_count", defaultValue: "{{updates_count}}" }
      ]
    }
  ],
  tags: ["updates", "changelog", "product", "engagement"],
  bestFor: ["All SaaS products", "Products with regular releases"],
  difficulty: "beginner",
  setupTime: "10 minutes"
};

export const INDUSTRY_INSIGHTS: SequenceTemplate = {
  id: "industry-insights",
  name: "Industry Insights & Trends",
  type: "education",
  description: "Position your brand as a thought leader with industry content",
  strategy: "Thought leadership builds trust and keeps you top-of-mind. HubSpot built an empire on educational content. Share insights beyond just your product.",
  icon: "LineChartOutlined",
  color: "#6366f1",
  expectedOutcomes: [
    "Brand authority building",
    "Email list growth",
    "Increased trust",
    "Social sharing"
  ],
  bestPractices: [
    "Focus on industry, not just product",
    "Include original data/research",
    "Make content shareable",
    "Feature expert opinions",
    "Tie back to product value"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 14, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Industry Report",
      subject: "2025 AI Productivity Report: Key Trends",
      suggestedTemplateId: "newsletter",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Share industry research and trends",
      variables: [
        { key: "report_title", defaultValue: "State of AI Copilots 2025" },
        { key: "key_stat", defaultValue: "78% of professionals use AI daily" }
      ]
    },
    {
      name: "Expert Roundup",
      subject: "What 10 experts predict for AI productivity",
      suggestedTemplateId: "newsletter",
      delayAfterPrevious: { value: 14, unit: "days" },
      purpose: "Expert opinions and predictions"
    },
    {
      name: "Best Practices Guide",
      subject: "The ultimate guide to AI copilot ROI",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 14, unit: "days" },
      purpose: "Comprehensive best practices"
    }
  ],
  tags: ["thought-leadership", "content", "industry", "education"],
  bestFor: ["B2B SaaS", "Emerging industries", "Brand building"],
  difficulty: "advanced",
  setupTime: "20 minutes"
};

export const EDUCATION_TEMPLATES = [
  WEEKLY_TIPS_SERIES,
  USE_CASE_INSPIRATION,
  PRODUCT_MASTERY_COURSE,
  WEBINAR_NURTURE,
  PRODUCT_UPDATES_DIGEST,
  INDUSTRY_INSIGHTS
];
